import { useMutation, useQueryClient } from "react-query";

import { unarchiveGoal } from "api/goals";
import { useGlobalToast } from "components/GlobalToastProvider";
import { GET_GOAL_BY_ID, GET_LIST_OF_GOALS } from "@constants/reactQueryKeys";

interface UnarchiveGoalPayload {
  goalId: number;
  clientId: string;
}

/**
 * Restores an archived goal. Unlike archiving, this needs no target
 * dispositions — the goal and its targets simply return to the active list.
 * Both the client's goal list and the goal detail query are invalidated.
 */
const useUnarchiveGoal = (options: any = {}) => {
  const { onSuccess = () => {}, onError = () => {}, ...otherOptions } = options;
  const queryClient = useQueryClient();
  const { showToast } = useGlobalToast();

  const {
    mutate: unarchiveGoalAction,
    isLoading: isUnarchivingGoal,
    ...rest
  } = useMutation(
    ({ goalId }: UnarchiveGoalPayload) => unarchiveGoal(goalId),
    {
      onSuccess: (response, variables) => {
        showToast({ message: "Goal unarchived" });

        queryClient.invalidateQueries([GET_LIST_OF_GOALS, variables.clientId]);
        queryClient.invalidateQueries([GET_GOAL_BY_ID, variables.goalId]);

        onSuccess(response, variables);
      },
      onError: (error: any) => {
        const errorMessage = error?.response?.data?.error || "Failed to unarchive goal";
        showToast({ message: errorMessage, errorState: true });

        onError(error);
      },
      ...otherOptions,
    }
  );

  return {
    unarchiveGoalAction,
    isUnarchivingGoal,
    ...rest,
  };
};

export default useUnarchiveGoal;
